import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Lock, Eye, EyeOff } from "lucide-react";
import "./authStyles.css";
import bgi from "../assets/Apricverseuse.jpeg";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { token: tokenParam } = useParams(); // Get token from URL path: /forgotpassword/:token
  const [searchParams] = useSearchParams();
  const { resetPassword } = useAuth();
  const [formData, setFormData] = useState({
    login: '',
    token: '',
    password: '',
    password_confirmation: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Get token from URL path parameter (e.g., /forgotpassword/{token})
    const tokenFromPath = tokenParam;
    
    // Get email/login from query params (e.g., ?email=baggoody10@gmail.com)
    const emailFromQuery = searchParams.get('email');
    
    // Get token and login from query params if available (fallback for /reset-password route)
    const tokenFromQuery = searchParams.get('token');
    const loginFromQuery = searchParams.get('login');
    
    // Prefer path token over query token, prefer email query param over login query param
    const finalToken = tokenFromPath || tokenFromQuery || '';
    const finalLogin = emailFromQuery || loginFromQuery || '';
    
    if (finalToken) {
      setFormData(prev => ({ ...prev, token: finalToken }));
    }
    if (finalLogin) {
      setFormData(prev => ({ ...prev, login: finalLogin }));
    }
  }, [tokenParam, searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    if (formData.password !== formData.password_confirmation) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setIsLoading(false);
      return;
    }
    
    try {
      const result = await resetPassword(
        formData.login,
        formData.token,
        formData.password,
        formData.password_confirmation
      );
      if (result.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(result.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-image">
        <img src={bgi} alt="Agriculture imagery" />
        <div className="absolute-logo">
          <img
            src="/AGRICVERSE_DARK.png"
            alt="AgricVerse Logo"
            style={{ height: 60, cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        </div>
      </div>
      <div className="auth-form-container">
        <div className="auth-form-wrapper">
          <div className="form-section active">
            <div className="form-header">
              <h1 className="form-title">Reset Password</h1>
              <p className="form-subtitle">
                Enter your new password
              </p>
            </div>

            {error && (
              <div className="error-text" style={{
                backgroundColor: '#fee2e2',
                border: '1px solid #fca5a5',
                color: '#991b1b',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                {error}
              </div>
            )}

            {success ? (
              <div style={{
                backgroundColor: '#dcfce7',
                border: '1px solid #86efac',
                color: '#166534',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <p style={{ margin: 0, fontWeight: '500' }}>
                  Your password has been reset successfully. Redirecting to login...
                </p>
              </div>
            ) : (
              <form className="auth-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="reset-login">Email or Phone Number</label>
                  <div className="input-group">
                    <input
                      type="text"
                      id="reset-login"
                      name="login"
                      value={formData.login}
                      onChange={handleInputChange}
                      placeholder="Enter your email or phone number"
                      required
                      disabled={isLoading || !!formData.login}
                      readOnly={!!formData.login}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="reset-token">Reset Token</label>
                  <div className="input-group">
                    <input
                      type="text"
                      id="reset-token"
                      name="token"
                      value={formData.token}
                      onChange={handleInputChange}
                      placeholder="Enter the token from your email"
                      required
                      disabled={isLoading || !!formData.token}
                      readOnly={!!formData.token}
                    />
                  </div>
                  {formData.token && (
                    <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      Token loaded from reset link
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="reset-password">New Password</label>
                  <div className="input-group">
                    <Lock size={18} />
                    <input
                      type={showPassword ? "text" : "password"}
                      id="reset-password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter your new password"
                      required
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="reset-password-confirmation">Confirm New Password</label>
                  <div className="input-group">
                    <Lock size={18} />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="reset-password-confirmation"
                      name="password_confirmation"
                      value={formData.password_confirmation}
                      onChange={handleInputChange}
                      placeholder="Confirm your new password"
                      required
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={isLoading}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <button 
                  type="submit" 
                  className="primary-button"
                  disabled={isLoading}
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            )}

            <div className="form-footer">
              <p>
                Remember your password?{" "}
                <button
                  onClick={() => navigate("/login")}
                  className="link-button"
                  disabled={isLoading}
                >
                  Login
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

